import { Dialog, Transition } from '@headlessui/react'
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  PhotoIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  CommandLineIcon,
  ComputerDesktopIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { Fragment, useState } from 'react'
import { exportAPI } from '../../services/api'

interface BatchExportModalProps {
  selectedProjectIds: string[]
  onClose: () => void
  onExportComplete: () => void
}

type ExportMode = 'gui' | 'cli'

export default function BatchExportModal({
  selectedProjectIds,
  onClose,
  onExportComplete,
}: BatchExportModalProps) {
  const [exportType, setExportType] = useState<'images' | 'annotations'>('images')
  const [mergeAnnotations, setMergeAnnotations] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exportMode, setExportMode] = useState<ExportMode>('gui')
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null)

  // Resolve API base URL for CLI snippets and requests
  const envUrl = (import.meta.env.VITE_API_URL || '').trim()
  const apiBaseUrl = (envUrl || window.location.origin).replace(/\/+$/, '')

  const handleExport = async () => {
    try {
      setExporting(true)
      setError(null)

      let response
      if (exportType === 'images') {
        response = await exportAPI.batchExportImages(selectedProjectIds)
      } else {
        response = await exportAPI.batchExportAnnotations(selectedProjectIds, mergeAnnotations)
      }

      // Handle file download
      const blob = new Blob([response.data], {
        type:
          response.headers?.['content-type'] ||
          (exportType === 'images'
            ? 'application/zip'
            : mergeAnnotations
              ? 'application/json'
              : 'application/zip'),
      })

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers?.['content-disposition']
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/)
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      const defaultExt = exportType === 'images' ? 'zip' : mergeAnnotations ? 'json' : 'zip'
      const filename = filenameMatch
        ? filenameMatch[1]
        : `batch_export_${exportType}_${timestamp}.${defaultExt}`

      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      onExportComplete()
    } catch (err: any) {
      console.error('Export error:', err)
      setError(err.response?.data?.detail || 'Failed to export. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  // Generate CURL commands
  const generateCurlCommand = () => {
    const token = localStorage.getItem('token')
    const apiUrl = apiBaseUrl
    const endpoint =
      exportType === 'images' ? '/api/v1/export/batch/images' : '/api/v1/export/batch/annotations'

    const outputFile =
      exportType === 'images'
        ? 'batch_export_images.zip'
        : mergeAnnotations
          ? 'batch_export_annotations.json'
          : 'batch_export_annotations.zip'

    // Build request body
    const requestBody =
      exportType === 'images'
        ? { project_ids: selectedProjectIds }
        : { project_ids: selectedProjectIds, merge_annotations: mergeAnnotations }

    const curlCommand = `curl -X POST \\
  "${apiUrl}${endpoint}" \\
  -H "Authorization: Bearer ${token || 'YOUR_JWT_TOKEN'}" \\
  -H "Content-Type: application/json" \\
  -H "Accept: ${exportType === 'images' ? 'application/octet-stream' : mergeAnnotations ? 'application/json' : 'application/octet-stream'}" \\
  -d '${JSON.stringify(requestBody, null, 2)}' \\
  -o "${outputFile}" \\
  -L`

    return curlCommand
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedCommand('batch-export')
      setTimeout(() => setCopiedCommand(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <Transition appear show as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-xl font-semibold text-gray-900">
                    Batch Export Projects
                  </Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Export Mode Selector */}
                  <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 p-1">
                    <button
                      onClick={() => setExportMode('gui')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        exportMode === 'gui'
                          ? 'bg-blue-50 text-blue-700 shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <ComputerDesktopIcon className="w-5 h-5" />
                      Browser Download
                    </button>
                    <button
                      onClick={() => setExportMode('cli')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        exportMode === 'cli'
                          ? 'bg-blue-50 text-blue-700 shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <CommandLineIcon className="w-5 h-5" />
                      CURL Command
                    </button>
                  </div>

                  {/* Selected Projects Info */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5" />
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-blue-900">
                          {selectedProjectIds.length} project
                          {selectedProjectIds.length !== 1 ? 's' : ''} selected for export
                        </p>
                        <p className="text-sm text-blue-700 mt-1">
                          The export will combine data from all selected projects into a single
                          download.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Export Type Selection - Show in both modes */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-3 block">
                      Select Export Type
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setExportType('images')}
                        className={`
                          relative rounded-lg border p-4 flex flex-col items-center text-center
                          ${
                            exportType === 'images'
                              ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50'
                              : 'border-gray-300 hover:border-gray-400'
                          }
                        `}
                      >
                        <PhotoIcon
                          className={`h-8 w-8 mb-2 ${
                            exportType === 'images' ? 'text-blue-600' : 'text-gray-400'
                          }`}
                        />
                        <span
                          className={`text-sm font-medium ${
                            exportType === 'images' ? 'text-blue-900' : 'text-gray-900'
                          }`}
                        >
                          Export Images
                        </span>
                        <span
                          className={`text-xs mt-1 ${
                            exportType === 'images' ? 'text-blue-700' : 'text-gray-500'
                          }`}
                        >
                          Download all images as ZIP
                        </span>
                      </button>

                      <button
                        onClick={() => setExportType('annotations')}
                        className={`
                          relative rounded-lg border p-4 flex flex-col items-center text-center
                          ${
                            exportType === 'annotations'
                              ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50'
                              : 'border-gray-300 hover:border-gray-400'
                          }
                        `}
                      >
                        <DocumentTextIcon
                          className={`h-8 w-8 mb-2 ${
                            exportType === 'annotations' ? 'text-blue-600' : 'text-gray-400'
                          }`}
                        />
                        <span
                          className={`text-sm font-medium ${
                            exportType === 'annotations' ? 'text-blue-900' : 'text-gray-900'
                          }`}
                        >
                          Export Annotations
                        </span>
                        <span
                          className={`text-xs mt-1 ${
                            exportType === 'annotations' ? 'text-blue-700' : 'text-gray-500'
                          }`}
                        >
                          Download annotations in COCO format
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Annotation Export Options - Show for annotations in both modes */}
                  {exportType === 'annotations' && (
                    <div className="border-t pt-4">
                      <label className="text-sm font-medium text-gray-700 mb-3 block">
                        Annotation Export Options
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-start">
                          <input
                            type="radio"
                            checked={mergeAnnotations}
                            onChange={() => setMergeAnnotations(true)}
                            className="mt-1 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-900">
                              Merge into single file
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              Combine all projects' annotations into one JSON file with project
                              metadata
                            </p>
                          </div>
                        </label>
                        <label className="flex items-start">
                          <input
                            type="radio"
                            checked={!mergeAnnotations}
                            onChange={() => setMergeAnnotations(false)}
                            className="mt-1 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-900">
                              Separate files per project
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              Export each project's annotations as individual JSON files in a ZIP
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* CLI Mode - CURL Command */}
                  {exportMode === 'cli' && (
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="flex items-center gap-3 mb-3">
                          {exportType === 'images' ? (
                            <PhotoIcon className="w-6 h-6 text-blue-600" />
                          ) : (
                            <DocumentTextIcon className="w-6 h-6 text-green-600" />
                          )}
                          <h4 className="text-base font-medium text-gray-900">
                            Batch Export {exportType === 'images' ? 'Images' : 'Annotations'} - CURL
                            Command
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                          Use this command to export {selectedProjectIds.length} selected project
                          {selectedProjectIds.length !== 1 ? 's' : ''} from your terminal
                          {exportType === 'annotations' && (
                            <span className="font-medium">
                              {mergeAnnotations
                                ? ' (merged into single JSON file)'
                                : ' (separate JSON files in ZIP)'}
                            </span>
                          )}
                          :
                        </p>
                        <div className="relative">
                          <pre className="bg-gray-900 text-gray-300 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                            <code className="text-gray-300">{generateCurlCommand()}</code>
                          </pre>
                          <button
                            onClick={() => copyToClipboard(generateCurlCommand())}
                            className="absolute top-2 right-2 p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-md transition-colors"
                            title="Copy command"
                          >
                            {copiedCommand === 'batch-export' ? (
                              <CheckIcon className="w-5 h-5 text-green-400" />
                            ) : (
                              <ClipboardDocumentIcon className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        {!localStorage.getItem('token') && (
                          <p className="mt-2 text-xs text-amber-600">
                            ⚠️ Note: Replace YOUR_JWT_TOKEN with your actual authentication token
                          </p>
                        )}
                      </div>

                      {/* CLI Instructions */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-blue-900 mb-2">
                          Using CURL Commands
                        </h4>
                        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                          <li>
                            This command will export data from all {selectedProjectIds.length}{' '}
                            selected projects
                          </li>
                          <li>
                            The <code className="bg-blue-100 px-1 rounded">-L</code> flag follows
                            redirects if needed
                          </li>
                          <li>Output file will be saved in your current directory</li>
                          <li>
                            You can modify the output filename with the{' '}
                            <code className="bg-blue-100 px-1 rounded">-o</code> parameter
                          </li>
                          {exportType === 'annotations' && (
                            <>
                              <li>
                                Export format:{' '}
                                {mergeAnnotations
                                  ? 'Single JSON file with all projects combined'
                                  : 'ZIP archive with separate JSON files per project'}
                              </li>
                              <li>
                                The{' '}
                                <code className="bg-blue-100 px-1 rounded">merge_annotations</code>{' '}
                                parameter is set to {mergeAnnotations.toString()}
                              </li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Export Details - Show in GUI mode */}
                  {exportMode === 'gui' && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Export Details</h4>
                      <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                        {exportType === 'images' ? (
                          <>
                            <li>All images will be packaged in a single ZIP file</li>
                            <li>Each project's images will be in a separate folder</li>
                            <li>Original filenames will be preserved</li>
                          </>
                        ) : (
                          <>
                            <li>Only annotations from completed users are included</li>
                            <li>Annotations are averaged across multiple annotators</li>
                            <li>Export format: COCO JSON with keypoints and bounding boxes</li>
                            {mergeAnnotations ? (
                              <li>All projects combined with unique IDs and project metadata</li>
                            ) : (
                              <li>Each project folder contains its own annotations.json file</li>
                            )}
                          </>
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={onClose}
                    disabled={exporting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {exportMode === 'cli' ? 'Close' : 'Cancel'}
                  </button>
                  {exportMode === 'gui' && (
                    <button
                      onClick={handleExport}
                      disabled={exporting}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                      {exporting
                        ? 'Exporting...'
                        : `Export ${exportType === 'images' ? 'Images' : 'Annotations'}`}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
